import { useCallback, useState } from "react";

/**
 * Custom hook to manage data in sessionStorage or localStorage.
 */
type StorageType = "local" | "session";

export const useLocalStorage = <StoredValue>(
  key: string,
  initialValue: StoredValue,
  storageType: StorageType = "local",
) => {
  const storage = storageType === "session" ? window.sessionStorage : window.localStorage;

  // Get stored value from storage or use initial value
  const getStoredValue = useCallback((): StoredValue => {
    try {
      const item = storage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading from ${storageType}Storage:`, error);
      return initialValue;
    }
  }, [storage, key, initialValue, storageType]);

  const [stateValue, setStateValue] = useState<StoredValue>(getStoredValue);

  // Set value in storage
  const setValue = useCallback(
    (value: StoredValue | ((val: StoredValue) => StoredValue)) => {
      try {
        const prevValue = getStoredValue();
        const nextValue = value instanceof Function ? value(prevValue) : value;
        if (prevValue === nextValue) {
          return;
        }
        setStateValue(nextValue);
        storage.setItem(key, JSON.stringify(nextValue));
      } catch (error) {
        console.error(`Error writing to ${storageType}Storage:`, error);
      }
    },
    [storage, key, getStoredValue, storageType],
  );

  // Remove value from storage
  const removeValue = useCallback(() => {
    try {
      storage.removeItem(key);
      setStateValue(initialValue);
    } catch (error) {
      console.error(`Error removing from ${storageType}Storage:`, error);
    }
  }, [storage, key, initialValue, storageType]);

  return [stateValue, setValue, removeValue] as const;
};
